import {
  Component,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NavbarComponent } from './components/template-components/common/navbar/navbar.component';
import { ToastBarComponent } from './toast-bar/toast-bar.component';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus
} from '@azure/msal-browser';
import { GreyOverlayComponent } from './components/template-components/common/grey-overlay/grey-overlay.component';

import { AccountInfoService } from './services/account-info.service';
import { environment } from '../environments/environment';
import { SignalRService } from './services/signalr.service';

@Component({
    selector: 'app-root',
    imports: [
        RouterOutlet,
        NavbarComponent,
        CommonModule,
        ToastBarComponent,
        GreyOverlayComponent,
    ],
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  tokenExpiration: string = '';
  private readonly _destroying$ = new Subject<void>();

  currentUser: AccountInfo | undefined;

  ctrlPressed: boolean = false;

  apiScope: string = environment.apiScope;

  // RCA-487: the global top navbar is replaced by the channel editor's own
  // header (back button, search, actions) on routes that opt in via
  // `data: { hideNavbar: true }` - see app.routes.ts.
  hideNavbar = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private accountInfoService: AccountInfoService,
    private signalRService: SignalRService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
  }

  // On initialization of the page, display the page elements based on the user state
  ngOnInit(): void {
    this.signalRService.startNotificationConnection();
    this.signalRService.startTemplateConnection();
    this.signalRService.startCampaignConnection();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        // Walk to the deepest activated route - `data` isn't inherited automatically
        // for routes without children, so the leaf is where `hideNavbar` actually lives.
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        this.hideNavbar = !!route.snapshot.data['hideNavbar'];
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        // setting active user works only
        // after parent function passes
        this.setActiveUser();
      });

    // Used for storing and displaying token expiration
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        )
      )
      .subscribe((msg) => {
        this.tokenExpiration = (msg.payload as any).expiresOn;
        localStorage.setItem('tokenExpiration', this.tokenExpiration);
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        )
      )
      .subscribe((msg) => {
        console.log('Login in failed!', msg);
        // alert('Login in failed! Check console log!');
      });
  }

  private async getAccessTokenWithRoles(account: any) {
    try {
      // Define token request with the scope that includes roles
      const tokenRequest = {
        scopes: [this.apiScope],
        account: account,
        forceRefresh: false,
      };

      // Acquire token silently
      const response = await this.authService.instance.acquireTokenSilent(
        tokenRequest
      );

      this.setRolesFromToken(response);
    } catch (error) {
      console.error('Error acquiring access token:', error);

      // If silent acquisition fails, try interactive
      try {
        const interactiveRequest = {
          scopes: [this.apiScope],
          account: account,
        };

        const response = await this.authService.instance.acquireTokenPopup(
          interactiveRequest
        );

        this.setRolesFromToken(response);
      } catch (interactiveError) {
        console.error(
          'Interactive token acquisition failed:',
          interactiveError
        );
      }
    }
  }

  /**
   * Extracts user roles from a JWT access token contained in the authentication response.
   * Decodes the token, retrieves the roles, and stores them using the accountInfoService.
   *
   * @param response - The authentication result containing the JWT access token.
   */
  private setRolesFromToken(response: AuthenticationResult) {
    if (response.accessToken) {
      // Decode the JWT access token to extract roles
      const decodedToken: any = jwtDecode(response.accessToken);

      // Extract roles from the token
      if (decodedToken.roles) {
        const roles: string[] = decodedToken.roles;

        // Store roles in your user object or service
        roles.forEach((role) => {
          this.accountInfoService.setUserRoles(role);
        });
      }
    }
  }

  async setActiveUser() {
    const activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      this.currentUser = activeAccount;
      // console.log('Current active account:', activeAccount);

      // Get access token to extract roles
      await this.getAccessTokenWithRoles(activeAccount);
    } else {
      // Fall back to getAllAccounts() if no active account is set
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.authService.instance.setActiveAccount(accounts[0]);
        this.currentUser = accounts[0];
      }
    }

    this.accountInfoService.updateData(this.currentUser);
  }

  // // send account data to the service
  //   this.accountInfoService.updateData(this.currentUser);

  // Log the user out
  logout() {
    this.authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // check if the pressed key is 'Control'
    if (event.key === 'Control') {
      this.ctrlPressed = true;

      // remove focus on elements
      let doc = document.activeElement as HTMLElement;
      if (doc && doc.classList.contains('notification-text-input')) {
        doc?.blur();
      }

      // disable clicks on right-side div
      const allElements = document.querySelectorAll('.right-side');
      allElements.forEach((element) => {
        element.classList.add('disable-clicks');
      });
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    // if the released key is left ctrl, remove the class
    if (event.key === 'Control') {
      this.ctrlPressed = false;

      // enable clicks on right-side div
      const allElements = document.querySelectorAll('.right-side');
      allElements.forEach((element) => {
        element.classList.remove('disable-clicks');
      });
    }
  }
}
